import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";
import { generateUUID } from "@/utils/uuid";
import { toast } from "sonner";
import { useRealTimeAdaptiveDifficultyCA } from "./useRealTimeAdaptiveDifficultyCA";
import type { CurrentAffairsQuestion } from "./useCurrentAffairsAdaptive";

interface CurrentAffairsQuizState {
  questions: CurrentAffairsQuestion[];
  currentQuestionIndex: number;
  answers: (number | null)[];
  score: number;
  difficulty: "easy" | "medium" | "hard";
  isLoading: boolean;
  error: string | null;
  sessionId: string;
  dateFilter: Date | null;
  timePeriod: string | null;
  startTime: Date | null;
  endTime: Date | null;
  showTranslation: boolean;
  lastAnswerTime: number | null;
  questionStartTime: Date | null;
  answerTimes: number[];
  allAnsweredQuestions: CurrentAffairsQuestion[]; // Store all answered questions for review
  allAnswers: (number | null)[]; // Store all answers for review
}

export function useCurrentAffairsAdaptiveQuiz(initialDifficulty: "easy" | "medium" | "hard" = "medium") {
  const { userId } = useAuthSession();
  const [state, setState] = useState<CurrentAffairsQuizState>({
    questions: [],
    currentQuestionIndex: 0,
    answers: [],
    score: 0,
    difficulty: initialDifficulty,
    isLoading: false,
    error: null,
    sessionId: generateUUID(),
    dateFilter: null,
    timePeriod: null,
    startTime: null,
    endTime: null,
    showTranslation: false, // Translation feature disabled by default
    lastAnswerTime: null,
    questionStartTime: null,
    answerTimes: [],
    allAnsweredQuestions: [], // New field to track all questions shown to user
    allAnswers: [] // New field to track all answers for review
  });

  // Initialize the adaptive difficulty system
  const adaptiveDifficulty = useRealTimeAdaptiveDifficultyCA<CurrentAffairsQuizState>(
    state,
    setState,
    {
      startWithMedium: true,
      fastAnswerThresholdMs: 8000, // 8 seconds threshold for current affairs
      recentQuestionsWindow: 3, // Consider only last 3 questions
      scoreMultipliers: { easy: 1, medium: 1.5, hard: 2 }
    }
  );

  // Load questions from database
  const loadQuestions = useCallback(async (date?: Date | null, timePeriod?: string | null) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Query to get questions
      let query;
      
      if (date) {
        const dateStr = date.toISOString().split('T')[0];
        query = supabase
          .from('current_affairs_questions')
          .select('*')
          .eq('date', dateStr);
          
        if (timePeriod) {
          query = query.eq('time_period', timePeriod);
        }
        
        console.log(`[CAQ] Fetching questions for date ${dateStr} and period ${timePeriod || 'any'}`);
      } else {
        // Get most recent questions - increased limit to have enough questions
        query = supabase
          .from('current_affairs_questions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100); // Increased limit to ensure we have enough questions
          
        console.log(`[CAQ] Fetching recent questions`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        setState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: "No questions available for the selected filters" 
        }));
        return;
      }
      
      console.log(`[CAQ] Loaded ${data.length} questions`);
      
      // Format the questions
      const formattedQuestions: CurrentAffairsQuestion[] = data.map(q => {
        const options = [q.option_a, q.option_b, q.option_c, q.option_d].filter(Boolean);
        
        // Find the correct answer index
        let correctAnswerIndex = options.findIndex(opt => 
          opt?.toLowerCase().trim() === q.correct_answer?.toLowerCase().trim()
        );
        if (correctAnswerIndex === -1) correctAnswerIndex = 0;
        
        return {
          id: q.id,
          question: q.question,
          options: options,
          correctAnswer: correctAnswerIndex,
          difficulty: q.difficulty || initialDifficulty,
          date: q.date,
          timePeriod: q.time_period,
          explanation: q.explanation,
          topic: q.time_period // Use time_period as topic
        };
      });
      
      // Select total questions for the quiz - ensuring we have 20 questions
      const totalQuestionsNeeded = 20;

      // Randomize all questions first
      const randomizedQuestions = formattedQuestions.sort(() => Math.random() - 0.5);
      
      // Create an initial set with a mix of difficulties, prioritizing the initialDifficulty
      const initialDifficultyQuestions = randomizedQuestions
        .filter(q => q.difficulty === initialDifficulty)
        .slice(0, totalQuestionsNeeded);
      
      // If we don't have enough questions of the initial difficulty, fill with other difficulties
      let finalQuestions = initialDifficultyQuestions;
      
      if (initialDifficultyQuestions.length < totalQuestionsNeeded) {
        const remainingNeeded = totalQuestionsNeeded - initialDifficultyQuestions.length;
        const otherQuestions = randomizedQuestions
          .filter(q => q.difficulty !== initialDifficulty)
          .slice(0, remainingNeeded);
        
        finalQuestions = [...initialDifficultyQuestions, ...otherQuestions];
      }
      
      // Ensure we have exactly the number of questions needed
      finalQuestions = finalQuestions.slice(0, totalQuestionsNeeded);

      console.log(`[CAQ] Selected ${finalQuestions.length} questions for the quiz`);
      
      setState(prev => ({
        ...prev,
        questions: finalQuestions,
        answers: new Array(finalQuestions.length).fill(null),
        isLoading: false,
        dateFilter: date || null,
        timePeriod: timePeriod || null,
        startTime: new Date(),
        questionStartTime: new Date(),
        allAnsweredQuestions: [], // Reset the all answered questions array
        allAnswers: [] // Reset all answers array
      }));
      
    } catch (err) {
      console.error("[CAQ] Error loading questions:", err);
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: "Failed to load questions. Please try again." 
      }));
    }
  }, [initialDifficulty]);
  
  // Answer a question
  const answerQuestion = useCallback((answer: number | null) => {
    if (answer === null) return;
    
    // Get current question
    const currentQuestion = state.questions[state.currentQuestionIndex];
    if (!currentQuestion) return;
    
    // Calculate time taken to answer
    const now = Date.now();
    const questionStartTime = state.questionStartTime ? state.questionStartTime.getTime() : now;
    const timeElapsed = now - questionStartTime;
    
    // Update answer times
    const newAnswerTimes = [...state.answerTimes, timeElapsed];
    
    // Check if answer is correct
    const isCorrect = answer === currentQuestion.correctAnswer;
    
    // Update answers array for current question set
    const newAnswers = [...state.answers];
    newAnswers[state.currentQuestionIndex] = answer;
    
    // Add current question and answer to the all answered questions/answers arrays
    const newAllAnsweredQuestions = [...state.allAnsweredQuestions, currentQuestion];
    const newAllAnswers = [...state.allAnswers, answer];
    
    // Update state
    setState(prev => ({
      ...prev,
      answers: newAnswers,
      lastAnswerTime: now,
      answerTimes: newAnswerTimes,
      questionStartTime: new Date(), // Reset for next question
      allAnsweredQuestions: newAllAnsweredQuestions, // Track all questions
      allAnswers: newAllAnswers // Track all answers
    }));
    
    // Record answer in adaptive difficulty system
    adaptiveDifficulty.recordAnswerWithTime(isCorrect, timeElapsed);
    
    // Save result to database
    if (userId) {
      saveQuestionResult(currentQuestion.id, isCorrect, timeElapsed);
    }
    
    console.log(`[CAQ] Question answered: ${isCorrect ? 'Correct' : 'Incorrect'}, Time: ${timeElapsed}ms`);
    
  }, [state.questions, state.currentQuestionIndex, state.answers, 
      state.questionStartTime, state.answerTimes, userId, adaptiveDifficulty, 
      state.allAnsweredQuestions, state.allAnswers]);
  
  // Save question result to database
  const saveQuestionResult = useCallback(async (questionId: string | number, isCorrect: boolean, timeElapsed: number) => {
    if (!userId) return;
    
    try {
      await supabase.from('current_affairs_performance').insert({
        user_id: userId,
        quiz_session_id: state.sessionId,
        question_id: questionId.toString(),
        is_correct: isCorrect,
        time_taken: timeElapsed,
        difficulty: state.difficulty,
        topic: 'Current Affairs'
      });

      console.log(`[CAQ] Saved question result to database: questionId=${questionId}, isCorrect=${isCorrect}`);
    } catch (error) {
      console.error("[CAQ] Error saving question result:", error);
    }
  }, [userId, state.sessionId, state.difficulty]);
  
  // Move to next question
  const nextQuestion = useCallback(() => {
    if (state.currentQuestionIndex < state.questions.length - 1) {
      setState(prev => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        questionStartTime: new Date() // Reset time for new question
      }));
    } else {
      // Quiz complete
      setState(prev => ({
        ...prev,
        endTime: new Date()
      }));
    }
  }, [state.currentQuestionIndex, state.questions.length]);
  
  // Toggle translation
  const toggleTranslation = useCallback(() => {
    setState(prev => ({
      ...prev,
      showTranslation: !prev.showTranslation
    }));
    
    // Inform user that translations are disabled
    toast.info("Translation feature is currently disabled for Current Affairs quizzes");
  }, []);
  
  // Get quiz results
  const getResults = useCallback(() => {
    // Use all answered questions for results if available, otherwise fall back to current questions set
    const questionsForResults = state.allAnsweredQuestions.length > 0
      ? state.allAnsweredQuestions
      : state.questions;
      
    const answersForResults = state.allAnswers.length > 0
      ? state.allAnswers
      : state.answers;
    
    const totalAnswered = answersForResults.filter(a => a !== null).length;
    const correctCount = answersForResults.reduce((count, ans, idx) => {
      if (ans === null) return count;
      const correctAnswer = questionsForResults[idx]?.correctAnswer;
      return count + (ans === correctAnswer ? 1 : 0);
    }, 0);
    
    const percentage = totalAnswered > 0 
      ? Math.round((correctCount / totalAnswered) * 100) 
      : 0;
      
    // Calculate time spent
    const startTimeMs = state.startTime ? state.startTime.getTime() : 0;
    const endTimeMs = state.endTime ? state.endTime.getTime() : Date.now();
    const totalSeconds = Math.floor((endTimeMs - startTimeMs) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    // Group results by difficulty
    const difficultyBreakdown = questionsForResults.reduce((acc, q, idx) => {
      const answer = answersForResults[idx];
      const difficulty = q.difficulty || 'medium';
      
      if (!acc[difficulty]) {
        acc[difficulty] = { total: 0, correct: 0 };
      }
      
      if (answer !== null) {
        acc[difficulty].total++;
        if (answer === q.correctAnswer) {
          acc[difficulty].correct++;
        }
      }
      
      return acc;
    }, {} as Record<string, { total: number, correct: number }>);
    
    // Get date info
    const dateInfo = state.dateFilter 
      ? { date: state.dateFilter.toISOString().split('T')[0], timePeriod: state.timePeriod }
      : { date: 'Various dates', timePeriod: null };
      
    return {
      score: correctCount,
      totalAnswered,
      percentage,
      difficultyBreakdown,
      dateInfo,
      sessionId: state.sessionId,
      adaptiveDifficulty: state.difficulty,
      averageAnswerTime: state.answerTimes.length > 0 ? state.answerTimes.reduce((a, b) => a + b, 0) / state.answerTimes.length / 1000 : 0,
      timeSpent: { minutes, seconds },
      // Add the complete questions and answers for review
      allQuestions: questionsForResults,
      allAnswers: answersForResults
    };
  }, [state.answers, state.questions, state.startTime, state.endTime, 
      state.dateFilter, state.timePeriod, state.sessionId, state.difficulty,
      state.answerTimes, state.allAnsweredQuestions, state.allAnswers]);
  
  // NEW FUNCTION: Save complete quiz results to the database
  const saveQuizResults = useCallback(async () => {
    if (!userId) {
      console.log('[CAQ] Cannot save quiz results: No user ID');
      return false;
    }
    
    try {
      const results = getResults();
      
      // Get questions and answers for saving
      const questionsForResults = state.allAnsweredQuestions.length > 0
        ? state.allAnsweredQuestions
        : state.questions;
        
      const answersForResults = state.allAnswers.length > 0
        ? state.allAnswers
        : state.answers;
      
      console.log('[CAQ] Saving quiz results to database:', {
        sessionId: results.sessionId,
        questions: questionsForResults.length,
        answers: answersForResults.length,
        score: results.score,
        percentage: results.percentage
      });
      
      // Insert results into current_affairs_quiz_results table
      const { error } = await supabase.from('current_affairs_quiz_results').insert({
        user_id: userId,
        session_id: results.sessionId,
        questions: questionsForResults,
        answers: answersForResults,
        score: results.score,
        total_questions: questionsForResults.length,
        percentage: results.percentage,
        difficulty: state.difficulty,
        date_filter: state.dateFilter ? state.dateFilter.toISOString().split('T')[0] : null,
        time_period: state.timePeriod,
        completed_at: new Date()
      });
      
      if (error) {
        console.error('[CAQ] Error saving quiz results:', error);
        toast.error('Failed to save quiz history');
        return false;
      }
      
      console.log('[CAQ] Quiz results saved successfully');
      return true;
    } catch (err) {
      console.error('[CAQ] Exception saving quiz results:', err);
      toast.error('Failed to save quiz history');
      return false;
    }
  }, [userId, getResults, state.allAnsweredQuestions, state.allAnswers, 
      state.questions, state.answers, state.difficulty, state.dateFilter, 
      state.timePeriod]);
  
  // Reset quiz
  const resetQuiz = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentQuestionIndex: 0,
      answers: new Array(prev.questions.length).fill(null),
      score: 0,
      startTime: new Date(),
      endTime: null,
      questionStartTime: new Date(),
      answerTimes: [],
      sessionId: generateUUID(),
      allAnsweredQuestions: [], // Reset tracking of all answered questions
      allAnswers: [] // Reset all answers
    }));
  }, []);
  
  // Set date filter
  const setDateFilter = useCallback((date: Date | null, timePeriod?: string | null) => {
    loadQuestions(date, timePeriod || null);
  }, [loadQuestions]);
  
  // Load questions when component mounts
  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);
  
  return {
    state,
    answerQuestion,
    nextQuestion,
    toggleTranslation,
    getResults,
    resetQuiz,
    setDateFilter,
    adaptiveDifficulty,
    saveQuizResults // Export the new function
  };
}
